/**
 * NPCPlacements.ts - NPC position data for all locations
 * Maps NPC IDs to their locations and sprite assignments
 */

import { Location } from './StoryData';

export interface NPCPlacement {
  npcId: string;
  name: string;
  location: Location;
  x: number; // Tile X position
  y: number; // Tile Y position
  facing: 'up' | 'down' | 'left' | 'right';
  spriteClass?: string; // Character class sprite to use (FIGHTER, THIEF, MONK, etc.)
  customSprite?: string; // Or custom sprite ID for unique NPCs
  wanders?: boolean; // Does NPC move around?
  blockMovement?: boolean; // Does NPC block player movement?
}

// ============================================================================
// NPC PLACEMENTS BY LOCATION
// ============================================================================

export const NPC_PLACEMENTS: NPCPlacement[] = [
  // =========================================================================
  // CORNELIA - Starting Town (24x24)
  // Buildings: NW weapon (2-7,2-6), NE item (16-21,2-6)
  //            SW inn (2-7,17-21), SE magic (16-21,17-21)
  //            Houses: (2-5,9-12), (18-21,9-12)
  // Center plaza: (10-13,10-13)
  // Walkable grass areas: paths between buildings
  // =========================================================================
  {
    npcId: 'CORNELIA_OLD_MAN',
    name: 'Old Man',
    location: Location.CORNELIA,
    x: 10,
    y: 8, // North of center plaza on grass
    facing: 'down',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_WOMAN',
    name: 'Woman',
    location: Location.CORNELIA,
    x: 13,
    y: 15, // South of center plaza on grass
    facing: 'up',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_GUARD',
    name: 'Guard',
    location: Location.CORNELIA,
    x: 12,
    y: 8, // Near north road entrance
    facing: 'down',
    spriteClass: 'FIGHTER',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_SHOP_KEEPER',
    name: 'Shop Keeper',
    location: Location.CORNELIA,
    x: 11,
    y: 11, // Center plaza area
    facing: 'right',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_INN_KEEPER',
    name: 'Inn Keeper',
    location: Location.CORNELIA,
    x: 8,
    y: 16, // Between houses and inn
    facing: 'down',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_SCHOLAR',
    name: 'Scholar',
    location: Location.CORNELIA,
    x: 12,
    y: 20, // Near south exit
    facing: 'up',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },

  // =========================================================================
  // CORNELIA CASTLE
  // =========================================================================
  {
    npcId: 'KING',
    name: 'King of Cornelia',
    location: Location.CORNELIA_CASTLE,
    x: 8,
    y: 5,
    facing: 'down',
    spriteClass: 'KING',
    blockMovement: true,
  },
  {
    npcId: 'PRINCESS_SARA',
    name: 'Princess Sara',
    location: Location.CORNELIA_CASTLE,
    x: 10,
    y: 5,
    facing: 'down',
    spriteClass: 'PRINCESS',
    blockMovement: true,
  },
  {
    npcId: 'CASTLE_MINISTER',
    name: 'Minister',
    location: Location.CORNELIA_CASTLE,
    x: 6,
    y: 7,
    facing: 'right',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },

  // =========================================================================
  // PRAVOKA - Port Town (24x24)
  // Water: x:19-23 (full height)
  // Docks: x:16-18, y:4-19 (stone floor)
  // Buildings: NW weapon (2-7,2-6), Inn (2-8,9-13), Item shop (2-7,17-21), Tavern (10-14,17-21)
  // Lighthouse: (10-13,2-5) decorative
  // Walkable: grass areas, dock area
  // =========================================================================
  {
    npcId: 'PRAVOKA_SAILOR',
    name: 'Sailor',
    location: Location.PRAVOKA,
    x: 17,
    y: 10, // On dock area (stone floor)
    facing: 'left',
    spriteClass: 'THIEF',
    blockMovement: true,
  },
  {
    npcId: 'PRAVOKA_WOMAN',
    name: 'Woman',
    location: Location.PRAVOKA,
    x: 10,
    y: 8, // On grass between buildings
    facing: 'down',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'PRAVOKA_MERCHANT',
    name: 'Merchant',
    location: Location.PRAVOKA,
    x: 8,
    y: 15, // On grass between buildings
    facing: 'down',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },

  // =========================================================================
  // ELFHEIM - Elf Town (24x24)
  // Buildings: NW magic (2-7,2-6), NE weapon (16-21,2-6)
  //            SW inn (2-7,17-21), SE item (16-21,17-21)
  //            Small houses: (2-5,9-12), (18-21,9-12)
  // Center: grass/flowers (10-13,10-13)
  // Walkable: grass paths around buildings
  // =========================================================================
  {
    npcId: 'ELFHEIM_ELF_1',
    name: 'Elf',
    location: Location.ELFHEIM,
    x: 10,
    y: 8, // North path on grass
    facing: 'down',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'ELFHEIM_ELF_2',
    name: 'Elf',
    location: Location.ELFHEIM,
    x: 13,
    y: 15, // South path on grass
    facing: 'up',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'ELFHEIM_PRINCE',
    name: 'Elf Prince',
    location: Location.ELFHEIM,
    x: 12,
    y: 8, // Near center north
    facing: 'down',
    spriteClass: 'KING',
    blockMovement: true,
  },

  // =========================================================================
  // MELMOND - Town of Decay (20x20)
  // Buildings: NW clinic (2-7,2-6), NE weapon (12-17,2-6)
  //            SW inn (2-7,13-17), SE item (12-17,13-17)
  // Center: stone plaza (7-12,7-12)
  // Graveyard: y:1, x:8-11
  // =========================================================================
  {
    npcId: 'MELMOND_OLD_MAN',
    name: 'Old Man',
    location: Location.MELMOND,
    x: 8,
    y: 8, // On stone plaza
    facing: 'down',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },
  {
    npcId: 'MELMOND_SAGE',
    name: 'Sage',
    location: Location.MELMOND,
    x: 11,
    y: 10, // On stone plaza
    facing: 'left',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'MELMOND_WOMAN',
    name: 'Woman',
    location: Location.MELMOND,
    x: 9,
    y: 12, // South of plaza on grass
    facing: 'up',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },

  // =========================================================================
  // CRESCENT LAKE - Circle of Sages (22x22)
  // Lake: (9-13,7-12) water
  // Stone paths: N (7-15,5), S (7-15,13), W (7,5-13), E (15,5-13)
  // Buildings: West house (2-6,6-9), East house (16-20,6-9)
  //            North house (8-13,1-4), South house (8-13,15-18)
  //            Inn (2-7,14-18), Magic shop (14-19,14-18)
  // =========================================================================
  {
    npcId: 'ELDER_SAGE',
    name: 'Elder Sage',
    location: Location.CRESCENT_LAKE,
    x: 11,
    y: 5, // On north stone path
    facing: 'down',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_2',
    name: 'Second Sage',
    location: Location.CRESCENT_LAKE,
    x: 7,
    y: 7, // On west stone path (moved up from lake)
    facing: 'right',
    spriteClass: 'BLACK_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_3',
    name: 'Third Sage',
    location: Location.CRESCENT_LAKE,
    x: 15,
    y: 7, // On east stone path (moved up from lake)
    facing: 'left',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_4',
    name: 'Fourth Sage',
    location: Location.CRESCENT_LAKE,
    x: 11,
    y: 13, // On south stone path
    facing: 'up',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },

  // =========================================================================
  // GAIA - Mountain Town (18x18)
  // Buildings: NW inn (2-7,2-6), NE armor (10-15,2-6)
  //            SW item (2-7,11-15), SE house (10-15,11-15)
  // Center: stone plaza (7-10,7-10)
  // =========================================================================
  {
    npcId: 'GAIA_MAN',
    name: 'Man',
    location: Location.GAIA,
    x: 8,
    y: 8, // On stone plaza
    facing: 'down',
    spriteClass: 'FIGHTER',
    blockMovement: true,
  },
  {
    npcId: 'GAIA_WOMAN',
    name: 'Woman',
    location: Location.GAIA,
    x: 9,
    y: 10, // South of plaza on grass
    facing: 'up',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },

  // =========================================================================
  // ONRAC - Beach Resort (20x20)
  // Water: x:15-19 (full height)
  // Beach grass: x:12-14 (walkway)
  // Buildings: Inn (2-8,2-7), Item shop (2-7,9-13), Magic shop (2-7,15-18)
  //            Beach houses: (9-11,9-11), (9-11,14-16)
  // =========================================================================
  {
    npcId: 'ONRAC_WOMAN',
    name: 'Woman',
    location: Location.ONRAC,
    x: 9,
    y: 8, // On grass near buildings
    facing: 'down',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'ONRAC_FISHERMAN',
    name: 'Fisherman',
    location: Location.ONRAC,
    x: 13,
    y: 6, // On beach grass walkway, not in water
    facing: 'right',
    spriteClass: 'THIEF',
    blockMovement: true,
  },

  // =========================================================================
  // SPECIAL NPCs - Caves and Dungeons
  // =========================================================================
  {
    npcId: 'MATOYA',
    name: 'Matoya',
    location: Location.MATOYA_CAVE,
    x: 7,
    y: 7,
    facing: 'down',
    spriteClass: 'WITCH',
    blockMovement: true,
  },
  {
    npcId: 'SARDA',
    name: 'Sarda',
    location: Location.SARDA_CAVE,
    x: 8,
    y: 5,
    facing: 'down',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'BAHAMUT',
    name: 'Bahamut',
    location: Location.BAHAMUT_CAVE,
    x: 8,
    y: 6,
    facing: 'down',
    spriteClass: 'DRAGON',
    blockMovement: true,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all NPCs for a specific location
 */
export function getNPCsAtLocation(location: Location): NPCPlacement[] {
  return NPC_PLACEMENTS.filter(npc => npc.location === location);
}

/**
 * Get NPC by ID
 */
export function getNPCById(npcId: string): NPCPlacement | undefined {
  return NPC_PLACEMENTS.find(npc => npc.npcId === npcId);
}

/**
 * Get NPC at specific tile position
 */
export function getNPCAtPosition(
  location: Location,
  x: number,
  y: number
): NPCPlacement | undefined {
  return NPC_PLACEMENTS.find(
    npc => npc.location === location && npc.x === x && npc.y === y
  );
}

/**
 * Check if tile is blocked by an NPC
 */
export function isNPCBlocking(location: Location, x: number, y: number): boolean {
  const npc = getNPCAtPosition(location, x, y);
  return npc?.blockMovement ?? false;
}
